import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import SEOHead from "../../components/feature/SEOHead";
import ShowcaseHero from "./components/ShowcaseHero";
import ShowcaseCard from "./components/ShowcaseCard";
import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import { SHOWCASE_LISTINGS, CATEGORIES, type ShowcaseCategory } from "../../mocks/showcaseMocks";

interface Toast {
  id: number;
  seller: string;
}

const SITE_URL = import.meta.env.VITE_SITE_URL ?? "https://trackly.app";

const showcaseJsonLd = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "Flip Showcase — See Real Reseller Listings | Trackly",
  "description": "Browse the Trackly Flip Showcase — cards, sneakers, tech, and more. See what other resellers are flipping and express interest in deals.",
  "url": `${SITE_URL}/showcase`,
  "isPartOf": {
    "@type": "WebSite",
    "name": "Trackly",
    "url": SITE_URL,
  },
};

export default function Showcase() {
  const { isDark } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState<ShowcaseCategory>("All");
  const [interested, setInterested] = useState<Set<string>>(new Set());
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [toastCounter, setToastCounter] = useState(0);

  const filtered = activeCategory === "All"
    ? SHOWCASE_LISTINGS
    : SHOWCASE_LISTINGS.filter((l) => l.category === activeCategory);

  const totalInterests = SHOWCASE_LISTINGS.reduce((s, l) => s + l.interestedCount, 0);

  const dismissToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const handleInterest = (id: string, sellerName: string) => {
    setInterested((prev) => new Set(prev).add(id));
    const toastId = toastCounter + 1;
    setToastCounter(toastId);
    setToasts((prev) => [...prev.slice(-2), { id: toastId, seller: sellerName }]);
  };

  useEffect(() => {
    if (toasts.length === 0) return;
    const latest = toasts[toasts.length - 1];
    const timer = setTimeout(() => dismissToast(latest.id), 3500);
    return () => clearTimeout(timer);
  }, [toasts, dismissToast]);

  const handlePostFlip = () => {
    if (user) {
      navigate("/dashboard");
    } else {
      navigate("/dashboard?intent=pro");
    }
  };

  const CATEGORY_ICONS: Record<ShowcaseCategory, string> = {
    All: "ri-layout-grid-line",
    Cards: "ri-stack-line",
    Sneakers: "ri-footprint-line",
    Tech: "ri-cpu-line",
    Games: "ri-gamepad-line",
    Clothing: "ri-t-shirt-line",
    Computers: "ri-macbook-line",
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDark ? "bg-[#050505]" : "bg-[#f8f8f8]"}`}>
      <SEOHead
        title="Flip Showcase — Browse Real Reseller Listings | Trackly"
        description="Browse the Trackly Flip Showcase — cards, sneakers, tech, and more. See what other resellers are flipping and express interest in great deals."
        keywords="flip showcase, reseller listings, sports card flips, sneaker reseller marketplace, trackly showcase"
        canonical={`${SITE_URL}/showcase`}
        ogTitle="Trackly Flip Showcase — Real Listings from Real Resellers"
        ogDescription="Cards, sneakers, tech, games, and more. Browse what resellers are flipping and get notified when FlipIQ launches."
        jsonLd={showcaseJsonLd}
      />
      <Navbar />

      <ShowcaseHero
        totalListings={SHOWCASE_LISTINGS.length}
        totalInterests={totalInterests}
        onPostFlip={handlePostFlip}
      />

      <section className="pb-24 px-8 lg:px-16">
        <div className="max-w-7xl mx-auto">

          {/* Category filters */}
          <div className={`flex items-center gap-2 pb-10 overflow-x-auto scrollbar-hide`}>
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                type="button"
                onClick={() => setActiveCategory(cat)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full border font-inter text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap flex-shrink-0 ${
                  activeCategory === cat
                    ? isDark ? "bg-white text-black border-white" : "bg-[#111] text-white border-[#111]"
                    : isDark ? "bg-transparent text-[#666] border-white/8 hover:text-white hover:border-white/20" : "bg-transparent text-[#888] border-black/10 hover:text-[#111] hover:border-black/20"
                }`}
              >
                <i className={`${CATEGORY_ICONS[cat]} text-xs`} />
                {cat}
                {cat === "All" && (
                  <span className={`font-inter text-[9px] px-1.5 py-0.5 rounded-full ${activeCategory === cat ? isDark ? "bg-black/15 text-black/60" : "bg-white/20 text-white/70" : isDark ? "bg-white/8 text-[#555]" : "bg-black/8 text-[#aaa]"}`}>
                    {SHOWCASE_LISTINGS.length}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {filtered.map((listing) => (
              <ShowcaseCard
                key={listing.id}
                listing={listing}
                isInterested={interested.has(listing.id)}
                onInterest={handleInterest}
              />
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="flex flex-col items-center justify-center py-24 gap-4">
              <div className={`w-14 h-14 flex items-center justify-center rounded-2xl ${isDark ? "bg-white/5" : "bg-black/5"}`}>
                <i className={`ri-inbox-line text-2xl ${isDark ? "text-white/20" : "text-black/20"}`} />
              </div>
              <p className={`font-inter text-sm ${isDark ? "text-[#555]" : "text-[#aaa]"}`}>
                No listings in this category yet.
              </p>
            </div>
          )}

          {/* Community CTA banner */}
          <div className={`mt-16 rounded-2xl border p-10 flex flex-col md:flex-row items-center justify-between gap-6 ${isDark ? "bg-white/[0.02] border-white/6" : "bg-white border-black/8"}`}>
            <div>
              <h3 className={`font-syne text-2xl font-bold mb-2 ${isDark ? "text-white" : "text-[#111]"}`}>
                Got something to flip?
              </h3>
              <p className={`font-inter text-sm leading-relaxed ${isDark ? "text-[#555]" : "text-[#888]"}`}>
                When FlipIQ launches, you&apos;ll be able to post your own listings and get notified the moment someone shows interest. These example listings are just a taste of what&apos;s coming.
              </p>
            </div>
            <button
              type="button"
              onClick={handlePostFlip}
              className={`flex items-center gap-2 font-inter text-sm font-medium px-6 py-3 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap flex-shrink-0 ${isDark ? "bg-white text-black hover:bg-[#e0e0e0]" : "bg-[#111] text-white hover:bg-[#333]"}`}
            >
              <i className="ri-add-line" />
              Join Early Access
            </button>
          </div>
        </div>
      </section>

      <Footer />

      {/* Toast notifications */}
      <div className="fixed bottom-6 right-6 flex flex-col gap-2 z-50 pointer-events-none">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`flex items-center gap-3 px-4 py-3 rounded-xl border font-inter text-sm pointer-events-auto animate-in slide-in-from-bottom-2 fade-in duration-300 ${isDark ? "bg-[#111] border-white/10 text-white" : "bg-white border-black/10 text-[#111]"}`}
          >
            <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
              <i className="ri-notification-3-line text-sm text-amber-400" />
            </div>
            <p>
              <span className="font-medium">{toast.seller}</span>
              <span className={isDark ? " text-[#666]" : " text-[#888]"}> has been notified of your interest!</span>
            </p>
            <button
              type="button"
              onClick={() => dismissToast(toast.id)}
              className={`w-4 h-4 flex items-center justify-center cursor-pointer ml-1 flex-shrink-0 ${isDark ? "text-[#555] hover:text-white" : "text-[#ccc] hover:text-[#555]"}`}
            >
              <i className="ri-close-line text-xs" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
